/**
 * Component for StructuredMetadata
 *
 * This adds structured metadata to the DOM in such a way that Google can parse it for improved
 * search indexing and mobile deep linking.
 *
 * More information available at:
 *  - https://developers.google.com/search/docs/guides/intro-structured-data
 *  - https://developers.google.com/search/docs/data-types/music
 *
 * @module component/StructuredMetadata
 */

import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { getMusicIdFromPandoraId } from '../../lib/PandoraIdUtil';
import { arePropsDifferent } from '../../lib/PropsUtils';

export class StructuredMetadata extends React.Component {
  static propTypes = {
    pandoraId: PropTypes.string,
    twitterHandle: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    isArtist: PropTypes.bool,
    isAlbum: PropTypes.bool,
    isTrack: PropTypes.bool,
    isPlaylist: PropTypes.bool,
    playlistName: PropTypes.string,
    playlistOwnerName: PropTypes.string,
    numTracks: PropTypes.number,
    artistName: PropTypes.string,
    artistSeoToken: PropTypes.string,
    artistShareableUrlPath: PropTypes.string,
    albumTitle: PropTypes.string,
    shareableUrlPath: PropTypes.string,
    albumTracks: ImmutablePropTypes.listOf(
      ImmutablePropTypes.contains({
        songTitle: PropTypes.string,
        artistName: PropTypes.string,
        trackSeoToken: PropTypes.string,
      }),
    ),
    songTitle: PropTypes.string,
    trackSeoToken: PropTypes.string,
  };

  shouldComponentUpdate(nextProps) {
    return arePropsDifferent(nextProps, this.props);
  }

  getData = () => {
    if (this.props.isArtist) {
      return this.getDetailedArtistData();
    } else if (this.props.isAlbum) {
      return this.getAlbumData();
    } else if (this.props.isTrack) {
      return this.getDetailedTrackData();
    } else if (this.props.isPlaylist) {
      return this.getDetailedPlaylistData();
    }
    return {};
  };

  getArtistData = ({ artistSeoToken, artistName, shareableUrlPath }) => {
    return {
      '@type': 'MusicGroup',
      url:
        'https://www.pandora.com' +
        (shareableUrlPath ? shareableUrlPath : `/artist/${artistSeoToken}`),
      name: artistName,
    };
  };

  getDetailedArtistData = () => {
    const data = Object.assign(
      this.getArtistData({
        artistSeoToken: this.props.artistSeoToken,
        artistName: this.props.artistName,
      }),
      {
        '@context': 'http://schema.org',
        '@type': 'MusicGroup',
        url: `https://www.pandora.com/artist/${this.props.artistSeoToken}`,
        name: this.props.artistName,
        description: this.props.description,
        image: this.props.image,
        potentialAction: this.getMobileDeepLinkData(
          this.props.artistSeoToken,
          this.props.pandoraId,
        ),
      },
    );
    // The 'sameAs' property is defined as:
    // URL of a page that unambiguously identifies the artist or album. Example: Wikipedia.
    // (source: https://developers.google.com/search/docs/data-types/music)
    // If we have a Twitter handle for the artist, let's use that for external disambiguation
    // TODO: if it would help, we _could_ pass the Rovi artist id to the client to create a link to allmusic.com
    // or musicbrainz id to link to that site (or all of the above)
    if (this.props.twitterHandle) {
      data.sameAs = `https://twitter.com/${this.props.twitterHandle}`;
    }
    return data;
  };

  getAlbumData = () => {
    return {
      '@context': 'http://schema.org',
      '@type': 'MusicAlbum',
      url: `https://www.pandora.com${this.props.shareableUrlPath}`,
      name: this.props.albumTitle,
      description: this.props.description,
      image: this.props.image,
      byArtist: this.getArtistData({
        artistSeoToken: this.props.artistSeoToken,
        artistName: this.props.artistName,
        shareableUrlPath: this.props.artistShareableUrlPath,
      }),
      // Can't launch station by album seed, so just create deep link to artist station
      potentialAction: this.getMobileDeepLinkData(this.props.artistSeoToken, this.props.pandoraId),
      track: this.props.albumTracks
        ? this.props.albumTracks.map(track => {
            return this.getTrackData({
              songTitle: track.get('name'),
              artistName: track.get('artistName'),
              shareableUrlPath: track.get('shareableUrlPath'),
            });
          })
        : null,
      // TODO: 'sameAs' property (if available)
    };
  };

  getTrackData = ({ songTitle, artistName, trackSeoToken, shareableUrlPath }) => {
    return {
      '@type': 'MusicRecording',
      name: songTitle,
      byArtist: artistName,
      url:
        'https://www.pandora.com' +
        (shareableUrlPath ? shareableUrlPath : `/artist/${trackSeoToken}`),
    };
  };

  getDetailedTrackData = () => {
    return Object.assign(
      this.getTrackData({
        songTitle: this.props.songTitle,
        artistName: this.props.artistName,
        trackSeoToken: this.props.trackSeoToken,
      }),
      {
        '@context': 'http://schema.org',
        image: this.props.image,
      },
    );
  };

  getDetailedPlaylistData = () => {
    return {
      '@context': 'http://schema.org',
      '@type': 'MusicPlaylist',
      url: `https://www.pandora.com${this.props.shareableUrlPath}`,
      name: `${this.props.playlistName} - Playlist Created by ${this.props.playlistOwnerName}`,
      description: this.props.description,
      image: this.props.image,
      numTracks: this.props.numTracks,
    };
  };

  getMobileDeepLinkData = (seoToken, pandoraId) => {
    // musicId is NOT provided by Catalog, for this one case we will derive it from pandoraId.
    // There are old android clients that wont know what to do with a pandoraId in the createStation flow this metadata fragment triggers.
    // TODO: This component will go away when generation of this data moves to the server: RADIO-23613
    const musicId = getMusicIdFromPandoraId(pandoraId);
    return {
      '@type': 'ListenAction',
      target: [
        {
          '@type': 'EntryPoint',
          urlTemplate: `https://www.pandora.com/station/start/${seoToken}`,
          actionPlatform: [
            'http://schema.org/DesktopWebPlatform',
            'http://schema.org/IOSPlatform',
            'http://schema.org/AndroidPlatform',
          ],
          InLanguage: 'USD',
        },
        {
          '@type': 'EntryPoint',
          urlTemplate: `android-app://com.pandora.android/pandorav4/createStation?musicId=${musicId}&source=googdeep`,
          actionPlatform: 'http://schema.org/AndroidPlatform',
          InLanguage: 'USD',
        },
      ],
      expectsAcceptanceOf: {
        '@type': 'Offer',
        eligibleRegion: [
          {
            '@type': 'Country',
            name: 'US',
          },
          {
            '@type': 'Country',
            name: 'AU',
          },
          {
            '@type': 'Country',
            name: 'NZ',
          },
        ],
      },
    };
  };

  render() {
    return <script type='application/ld+json'>{JSON.stringify(this.getData())}</script>;
  }
}

export default StructuredMetadata;
